import { useLanguage } from '@/app/providers';
import { activeRightStack, closeSidePanel } from '@/app/store';
import { ChatCenteredDotsIcon, IconBoxArrowDown, InfoIconNew, MuteIcon, PinIcon, TrashIcon } from '@/assets/images';
import { AppIcon } from '@/components';
import { resetAlertModal, setAlertModal } from '@/components/AlertModalRoot/alertModalService';
import { CALL_MODES, CALLKIT_CALLSCREENSTATUS } from '@/features/calls/constants';
import { useCallkitDataKeys } from '@/features/calls/model';
import { endCall } from '@/features/calls/services/callService';
import { getLocalUserName } from '@/features/calls/utils/helper';
import { logger } from '@/shared/utils';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAmIParticipant } from '../model/groupParticipants';
import { archiveChatAction, deleteChatAction } from '../sub-features/chat-info/utils/actionUtils';
import { formatUserIdToJid } from '../utils';
import { useLastMessageDisplay } from './useLastMessageDisplay';

/**
 * ViewModel for ChatItem.
 * Owns: chat navigation, context menu items with their actions, last message display.
 * ChatItem.jsx renders only — no dispatch or logic allowed there.
 */
export const useChatItem = item => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const activeChatJid = formatUserIdToJid(item.fromUserId, item.chatType);
   const isArchived = item.archiveStatus === 1;
   const { lastMessageDisplay, isMyMessage } = useLastMessageDisplay(item);

   const {
      callMode,
      callType,
      callScreenStatus,
      ongoingCallParticipants = [],
   } = useCallkitDataKeys(['callMode', 'callType', 'callScreenStatus', 'ongoingCallParticipants']);
   const amIParticipant = useAmIParticipant(item.fromUserId);
   const localUser = getLocalUserName();
   const remoteUser = ongoingCallParticipants.find(p => p.userId !== localUser);

   const isOngoingCall =
      callMode === CALL_MODES.ONETOONE &&
      callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE &&
      remoteUser?.userId === item.fromUserId;

   const handleChatClick = useCallback(() => {
      dispatch(
         activeRightStack({
            activeStack: 'chats',
            activeChatId: item.fromUserId,
            activeChatJid,
         }),
      );
      dispatch(closeSidePanel());
   }, [dispatch, item.fromUserId, activeChatJid]);

   const handleEndCall = useCallback(e => {
      e.stopPropagation();
      endCall();
   }, []);

   const handleDeleteChat = useCallback(() => {
      const modalId = 'recent_chat_delete';
      const title = activeChatJid.includes('mix') ? t('chat.DELETE_GROUP') : t('chat.DELETE_CHAT');
      const typeLabel = activeChatJid.includes('mix')
         ? t('group.GROUP_NAME_LABEL').toLowerCase()
         : t('chat.CHATS_TITLE').toLowerCase().slice(0, -1);
      const description = t('chat.DELETE_DESC', { type: typeLabel });

      setAlertModal({
         id: modalId,
         visible: true,
         title,
         description,
         icon: <AppIcon icon={<TrashIcon className="alert-modal-icon ct-flexCenter" />} />,
         yesButton: t('chat.DELETE'),
         noButton: t('common.CANCEL'),
         yesAction: async () => {
            await deleteChatAction(activeChatJid);
            resetAlertModal(modalId);
         },
         onRequestClose: () => resetAlertModal(modalId),
         customClassName: 'alert-modal-delete-v2',
      });
   }, [activeChatJid, t]);

   // Context menu actions — defined in viewmodel, not in the UI file
   const chatMenuItems = useMemo(
      () =>
         [
            {
               id: 'info',
               icon: <InfoIconNew />,
               label: 'Info',
               action: () => logger.log('Info clicked'),
               show: false,
            },
            {
               id: 'pin',
               icon: <PinIcon />,
               label: 'Pin',
               action: () => logger.log('Pin clicked'),
               show: false,
            },
            {
               id: 'mute',
               icon: <MuteIcon />,
               label: 'Mute',
               action: () => logger.log('Mute clicked'),
               show: false,
            },
            {
               id: 'mark-unread',
               icon: <ChatCenteredDotsIcon />,
               label: 'Mark as Unread',
               action: () => logger.log('Mark as unread'),
               show: false,
            },
            {
               id: 'archive',
               icon: <AppIcon icon={<IconBoxArrowDown style={isArchived ? { transform: 'rotate(180deg)' } : {}} />} />,
               label: isArchived ? 'Unarchive Chat' : 'Archive Chat',
               action: () => archiveChatAction({ jid: activeChatJid, status: item.archiveStatus }),
               show: true,
            },
            {
               id: 'delete',
               icon: <TrashIcon />,
               label: 'Delete',
               action: handleDeleteChat,
               className: 'danger',
               show: activeChatJid.includes('mix') ? !amIParticipant : true,
            },
         ].filter(menuItem => menuItem.show),
      [isArchived, item.archiveStatus, activeChatJid, amIParticipant, handleDeleteChat],
   );

   return {
      handleChatClick,
      chatMenuItems,
      lastMessageDisplay,
      isOngoingCall,
      callType,
      callScreenStatus,
      handleEndCall,
      isMyMessage,
      activeChatJid,
   };
};
